// 단가표 표준 상품 칸 — 상태 배지·상품 이름, 대표면 연결·연결 끊기·올리기·품목만 표시(legacy 에 없는 화면이라 settings-* 밖에 둔다).
import { useTransition } from "react";
import { toast } from "sonner";

import StatusBadge, { type StatusTone } from "@buildpy/ui/status-badge";
import { Checkbox } from "@buildpy/ui/checkbox";
import { Button } from "@buildpy/ui/button";
import { CATALOG_LINK_LABEL, type CatalogLinkStatus } from "@buildpy/core/modules/catalog/types";
import { acceptCatalogSuggestion, setCatalogOptOut, unlinkCatalogProduct } from "@/api/actions/catalog";
import { useRouter } from "@/lib/nav";
import { settleRedirect } from "@/lib/redirect-error";

export const CATALOG_TONE: Record<CatalogLinkStatus, StatusTone> = {
  LINKED: "done",
  PENDING: "progress",
  SUGGESTED: "new",
  OPT_OUT: "dropped",
  UNLINKED: "hold",
};

type CatalogWork = () => Promise<{ ok: true } | { ok: false; message: string }>;

// 연결·품목만 저장 — 알리고 화면을 다시 읽는다(거절돼도 — 오래된 탭이 최신 상태로).
export const useCatalogRunner = () => {
  const router = useRouter();
  const [busy, start] = useTransition();
  const run = (work: CatalogWork, done: string) =>
    start(async () => {
      try {
        const result = await work();
        if (!result.ok) {
          toast.error(result.message);
          router.refresh();
          return;
        }
        toast.success(done);
        router.refresh();
      } catch (caught) {
        if (settleRedirect(caught)) return;
        toast.error("저장하지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  return { busy, run };
};

interface Props {
  row: { id: string; item: string; catalog?: { status: CatalogLinkStatus; product: { id: string; name: string } | null } };
  canEdit: boolean;
  busy: boolean;
  onRun: (work: CatalogWork, done: string) => void;
  onOpenSubmit: () => void;
}

const CatalogPriceCell = ({ row, canEdit, busy, onRun, onOpenSubmit }: Props) => {
  const status = row.catalog?.status ?? "UNLINKED";
  const product = row.catalog?.product ?? null;
  const open = status === "UNLINKED" || status === "SUGGESTED";
  return (
    <div className="flex min-w-0 flex-col items-start gap-y-1" data-catalog-status={status}>
      <StatusBadge label={CATALOG_LINK_LABEL[status]} tone={CATALOG_TONE[status]} />
      {product && (
        <span className="max-w-full break-words text-xs text-muted-foreground" data-catalog-product={product.id}>
          {status === "SUGGESTED" ? `제안 ${product.name}` : product.name}
        </span>
      )}
      {canEdit && status === "SUGGESTED" && product && (
        <Button
          aria-label={`${row.item} — ${product.name} 에 연결`}
          disabled={busy}
          size="xs-touch"
          type="button"
          variant="link"
          onClick={() => onRun(() => acceptCatalogSuggestion(row.id, product.id), `「${product.name}」 에 연결했습니다`)}>
          연결
        </Button>
      )}
      {canEdit && status === "LINKED" && (
        <Button
          aria-label={`${row.item} — 표준 상품 연결 끊기`}
          className="whitespace-normal text-left"
          disabled={busy}
          size="xs-touch"
          type="button"
          variant="link"
          onClick={() => onRun(() => unlinkCatalogProduct(row.id), "표준 상품 연결을 끊었습니다 — 단가는 그대로입니다")}>
          표준 상품 연결 끊기
        </Button>
      )}
      {canEdit && open && (
        <Button className="whitespace-normal text-left" disabled={busy} size="xs-touch" type="button" variant="link" onClick={onOpenSubmit}>
          표준 상품으로 올리기
        </Button>
      )}
      {canEdit && (open || status === "OPT_OUT") && (
        <label className="flex items-center gap-x-1.5 text-xs text-muted-foreground">
          <Checkbox
            aria-label={`${row.item} 품목만(시공 항목)`}
            checked={status === "OPT_OUT"}
            disabled={busy}
            onCheckedChange={(state) =>
              onRun(() => setCatalogOptOut(row.id, state === true), state === true ? "품목만(시공 항목)으로 표시했습니다" : "품목만 표시를 껐습니다")
            }
          />
          품목만(시공 항목)
        </label>
      )}
    </div>
  );
};

export default CatalogPriceCell;
